<template>
  <Pane :value="checklistShareByEmailPane.isOpen"
        :current-tab="checklistShareByEmailPane.tab"
        :tabs="tabs"
        eager
        temporary
        data-locator="checklist-share-by-mail-pane"
        @update:modelValue="checklistShareByEmailPane.isOpen = $event"
  >
    <template v-if="checklistShareByEmailPane.isOpen" #content-share-email>
      <v-row>
        <v-col cols="12">
          <h3 class="d-inline">{{$t('project.checklist.panes.ChecklistShareByEmailPane.selectFormat')}}</h3>
        </v-col>
      </v-row>
      <v-row class="mt-0">
        <v-col cols="12">
          <v-radio-group v-model="shareFormat" class="mt-0">
            <v-radio :value="shareFormatOptions.WORD" class="mr-12" data-locator="word-checklist-option">
              <template #label>
                <span>{{ $t('project.checklist.panes.ChecklistShareByEmailPane.formatWord') }}</span>
              </template>
            </v-radio>
            <v-radio :value="shareFormatOptions.PDF" data-locator="pdf-checklist-option">
              <template #label>
                <span>{{ $t('project.checklist.panes.ChecklistShareByEmailPane.formatPdf') }}</span>
              </template>
            </v-radio>
          </v-radio-group>
        </v-col>
      </v-row>

      <v-divider class="mt-2 mb-4" />

      <v-row class="mt-0">
        <v-col cols="12">
          <h3 class="d-inline">{{$t('project.checklist.panes.ChecklistShareByEmailPane.selectRecipients')}}</h3>
        </v-col>
      </v-row>
      <v-row class="mt-0">
        <v-col cols="12">
          <DocumentSharePicker :forcePmSelection="false" users-only />
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <v-row class="mt-0">
        <v-col cols="12">
          <h3 class="d-inline">{{$t('project.checklist.panes.ChecklistShareByEmailPane.addMessage')}}</h3>
        </v-col>
      </v-row>
      <v-row class="mt-0">
        <v-col cols="12">
          <VisualTextEditor
            v-model:value="notifyMessage"
            ref="notifyMessageEditor"
            counter="footer"
            :error-messages="notifyMessageErrors"
            :max-length="NOTIFY_MESSAGE_MAX_LENGTH"
            :placeholder="$t('project.checklist.panes.ChecklistShareByEmailPane.optionalMessage')"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6">
          <AppButton block
                    color="primary"
                    :disabled="shareChecklistByEmailPending"
                    variant="outlined"
                    data-locator="cancel-button"
                    @click="close"
          >
            {{$t('common.cancel')}}
          </AppButton>
        </v-col>
        <v-col cols="6">
          <AppButton block
                    color="primary"
                    :disabled="shareDisabled"
                    :loading="shareChecklistByEmailPending"
                    data-locator="confirm-button"
                    @click="shareByEmail"
          >
            {{$t('common.share')}}
          </AppButton>
        </v-col>
      </v-row>
    </template>
  </Pane>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AppButton from '@/common/buttons/AppButton.vue'
import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import Pane from '@/common/Pane.vue'
import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { CLOSE_CHECKLIST_SHARE_BY_EMAIL_PANE, SHARE_CHECKLIST_BY_EMAIL } from '@/store/modules/checklist/action_types'

const NOTIFY_MESSAGE_MAX_LENGTH = 400

export default {
  name: 'ChecklistShareByEmailPane',
  components: { AppButton, DocumentSharePicker, Pane, VisualTextEditor },
  data () {
    return {
      NOTIFY_MESSAGE_MAX_LENGTH,
      notifyMessage: '',
      shareFormat: '',
      shareFormatOptions: Object.freeze({
        WORD: 'todo-word',
        PDF: 'todo-pdf',
      }),
      tabs: [
        {
          title: this.$t('project.checklist.panes.ChecklistShareByEmailPane.tabShareByEmail'),
          id: 'share-email',
        },
      ],
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapState('checklist', ['checklistShareByEmailPane', 'shareChecklistByEmailPending']),
    ...mapState('documentSharePicker', ['selectedGroups', 'selectedMembers']),
    noRecipientsSelected () {
      return this.selectedGroups.length === 0 && this.selectedMembers.length === 0
    },
    notifyMessageLength () {
      return escapeHtml(this.notifyMessage).length
    },
    notifyMessageIsTooLong () {
      return this.notifyMessageLength > this.NOTIFY_MESSAGE_MAX_LENGTH
    },
    notifyMessageErrors () {
      const errors = []

      if (this.notifyMessageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    shareDisabled () {
      return this.notifyMessageIsTooLong || !this.shareFormat || this.noRecipientsSelected
    },
  },
  methods: {
    ...mapActions('checklist', [CLOSE_CHECKLIST_SHARE_BY_EMAIL_PANE, SHARE_CHECKLIST_BY_EMAIL]),
    close () {
      this.notifyMessage = ''
      this.$refs.notifyMessageEditor?.clear()
      this.CLOSE_CHECKLIST_SHARE_BY_EMAIL_PANE()
    },
    async shareByEmail () {
      const shareData = {
        type: this.shareFormat,
        withUsers: this.selectedMembers.map(i => i.id),
        withGroups: this.selectedGroups.map(i => i.id),
      }

      if (this.notifyMessage) {
        shareData.message = this.notifyMessage
      }

      try {
        await this.SHARE_CHECKLIST_BY_EMAIL({
          mnemo: this.roomMnemo,
          data: shareData,
        })

        toast.success(this.$t('project.checklist.panes.ChecklistShareByEmailPane.shareByEmailSuccess'))

        this.CLOSE_CHECKLIST_SHARE_BY_EMAIL_PANE()
      } catch (error) {
        console.error(error)

        toast.error(this.$t('project.checklist.panes.ChecklistShareByEmailPane.shareByEmailError'))
      }
    },
  },
}
</script>
